import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Input, Flex, Icon, Tooltip, Text, Wrap, WrapItem, Tag, TagLabel, TagCloseButton, useColorModeValue } from "@chakra-ui/react";
import { MainTable } from "../../../components/Table/MainTable";
import { useAuth0 } from "@auth0/auth0-react";
import { useEmiDetails } from "../../../lib/hooks";
import { flattenEmiDetails } from "../../../utils/knox";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { SidebarContext } from "../../../contexts/SidebarContext";
import FilterSystem, { getActiveFilterCount, getFilterDisplayValue, removeFilter as removeFilterHelper } from "../../../components/filters/FilterSystem";

function EmiDetails() {
  const { user, isLoading: userLoading } = useAuth0();
  const { sidebarCollapsed } = useContext(SidebarContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);

  useEffect(() => {
    const token = localStorage.getItem("auth0_token");
    if (user && !userLoading && token) setMounted(true);
  }, [user, userLoading]);

  const { emiDetails, isEmiDetailsLoading } = useEmiDetails(mounted);
  const source = Array.isArray(emiDetails?.body) ? emiDetails.body : Array.isArray(emiDetails) ? emiDetails : [];

  const flatRows = useMemo(() => flattenEmiDetails(source), [source]);

  const baseAccessors = [
    "invoiceNo",
    "invoiceDate",
    "serialNumber",
    "itemName",
    "paymentTerms",
    "salesAgentCode",
    "knoxStatus",
    "daysOverdue",
  ];
  const emiAccessors = Array.from({ length: 6 }, (_, i) => i + 1).flatMap((k) => [
    `emi${k}_dueDate`,
    `emi${k}_expected`,
    `emi${k}_paid`,
    `emi${k}_paidDate`,
    `emi${k}_status`,
  ]);
  const allAccessors = [...baseAccessors, ...emiAccessors];

  const [filters, setFilters] = useState({});
  const [sortKey, setSortKey] = useState("invoiceDate");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");

  const knoxStatuses = useMemo(
    () => Array.from(new Set(flatRows.map((r) => r.knoxStatus).filter(Boolean))).sort(),
    [flatRows]
  );
  const tenureOptions = useMemo(
    () => Array.from(new Set(flatRows.map((r) => r.paymentTerms).filter((x)=>x!==null && x!==undefined))).sort((a,b)=>a-b),
    [flatRows]
  );
  const emiStatusOptions = ["paid", "pending", "overdue"];
  const salesAgents = useMemo(
    () => Array.from(new Set(flatRows.map((r) => r.salesAgentCode).filter(Boolean))).sort(),
    [flatRows]
  );

  const filterFields = useMemo(
    () => [
      {
        key: "knoxStatus",
        label: "Knox Status",
        type: "multiselect",
        options: knoxStatuses,
      },
      {
        key: "paymentTerms",
        label: "Payment Tenure",
        type: "multiselect",
        options: tenureOptions.map((t) => ({ value: String(t), label: `${t} Months` })),
      },
      {
        key: "emiStatus",
        label: "EMI Status (any)",
        type: "multiselect",
        options: emiStatusOptions,
      },
      {
        key: "invoiceNo",
        label: "Invoice Number",
        type: "text",
        placeholder: "Enter invoice number",
      },
      {
        key: "invoiceDate",
        label: "Invoice Date",
        type: "daterange",
      },
      {
        key: "serialNumber",
        label: "Serial Number",
        type: "text",
        placeholder: "Enter serial number",
      },
      {
        key: "salesAgentCode",
        label: "Sales Agent",
        type: "multiselect",
        options: salesAgents,
      },
      {
        key: "daysOverdue",
        label: "Days Overdue",
        type: "number",
        placeholder: "Min days overdue",
      },
    ],
    [knoxStatuses, tenureOptions, salesAgents]
  );

  const filtered = useMemo(() => {
    const toStr = (v) => (v === null || v === undefined ? "" : String(v)).toLowerCase();
    return flatRows.filter((r) => {
      // Knox status filter (multiselect)
      if (filters.knoxStatus && Array.isArray(filters.knoxStatus) && filters.knoxStatus.length > 0) {
        if (!filters.knoxStatus.includes(r.knoxStatus)) {
          return false;
        }
      }

      // Payment terms filter (multiselect)
      if (filters.paymentTerms && Array.isArray(filters.paymentTerms) && filters.paymentTerms.length > 0) {
        if (!filters.paymentTerms.includes(String(r.paymentTerms))) {
          return false;
        }
      }

      // EMI status filter (multiselect) - check if any EMI has any of the selected statuses
      if (filters.emiStatus && Array.isArray(filters.emiStatus) && filters.emiStatus.length > 0) {
        let anyMatch = false;
        for (let i = 1; i <= 6; i++) {
          if (filters.emiStatus.includes(r[`emi${i}_status`])) {
            anyMatch = true;
            break;
          }
        }
        if (!anyMatch) return false;
      }

      // Invoice number filter
      if (filters.invoiceNo && !toStr(r.invoiceNo).includes(toStr(filters.invoiceNo))) {
        return false;
      }

      // Invoice date range filter
      if (filters.invoiceDate_from || filters.invoiceDate_to) {
        const invDate = r.invoiceDate;
        if (invDate) {
          if (filters.invoiceDate_from && invDate < filters.invoiceDate_from) {
            return false;
          }
          if (filters.invoiceDate_to && invDate > filters.invoiceDate_to) {
            return false;
          }
        }
      }

      // Serial number filter
      if (filters.serialNumber && !toStr(r.serialNumber).includes(toStr(filters.serialNumber))) {
        return false;
      }

      // Sales agent filter (multiselect)
      if (filters.salesAgentCode && Array.isArray(filters.salesAgentCode) && filters.salesAgentCode.length > 0) {
        if (!filters.salesAgentCode.includes(r.salesAgentCode)) {
          return false;
        }
      }

      // Days overdue filter (minimum)
      if (filters.daysOverdue) {
        const minDays = Number(filters.daysOverdue);
        if (!isNaN(minDays) && (r.daysOverdue || 0) < minDays) {
          return false;
        }
      }

      // Search filter
      if (search) {
        const hay = allAccessors.map((a) => toStr(r[a])).join(" ");
        if (!hay.includes(search.toLowerCase())) return false;
      }

      return true;
    });
  }, [flatRows, filters, search, allAccessors]);

  const sortedAll = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = a?.[sortKey];
      const vb = b?.[sortKey];
      const na = typeof va === "number" ? va : isNaN(Number(va)) ? null : Number(va);
      const nb = typeof vb === "number" ? vb : isNaN(Number(vb)) ? null : Number(vb);
      let cmp;
      if (na !== null && nb !== null) cmp = na - nb;
      else cmp = String(va ?? "").localeCompare(String(vb ?? ""));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalCount = sortedAll.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const pageSlice = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedAll.slice(start, start + perPage);
  }, [sortedAll, page, perPage]);

  const handleHeaderSort = (accessor) => {
    if (sortKey === accessor) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(accessor);
      setSortDir("asc");
    }
  };

  const columns = useMemo(() => {
    const headerNode = (label, accessor) => (
      <Flex align="center" onClick={() => handleHeaderSort(accessor)} cursor="pointer" whiteSpace="nowrap">
        {label} {sortKey === accessor && (<Icon as={sortDir === "asc" ? ChevronUpIcon : ChevronDownIcon} ml={1} />)}
      </Flex>
    );
    const base = [
      {
        Header: () => headerNode("Invoice No", "invoiceNo"),
        accessor: "invoiceNo",
        minWidth: "120px"
      },
      {
        Header: () => headerNode("Invoice Date", "invoiceDate"),
        accessor: "invoiceDate",
        minWidth: "120px"
      },
      {
        Header: () => headerNode("Serial Number", "serialNumber"),
        accessor: "serialNumber",
        minWidth: "150px"
      },
      {
        Header: () => headerNode("Item Name", "itemName"),
        accessor: "itemName",
        minWidth: "300px",
        Cell: ({ value }) => (
          <Tooltip label={value} placement="top" hasArrow>
            <Text
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              maxW="400px"
            >
              {value}
            </Text>
          </Tooltip>
        )
      },
      {
        Header: () => headerNode("Tenure", "paymentTerms"),
        accessor: "paymentTerms",
        minWidth: "80px"
      },
      {
        Header: () => headerNode("Sales Agent", "salesAgentCode"),
        accessor: "salesAgentCode",
        minWidth: "120px"
      },
      {
        Header: () => headerNode("Knox Status", "knoxStatus"),
        accessor: "knoxStatus",
        minWidth: "120px"
      },
      {
        Header: () => headerNode("Days Overdue", "daysOverdue"),
        accessor: "daysOverdue",
        minWidth: "120px"
      },
    ];
    const emiCols = [];
    for (let i = 1; i <= 6; i++) {
      emiCols.push({ Header: () => headerNode(`EMI ${i} Due`, `emi${i}_dueDate`), accessor: `emi${i}_dueDate`, minWidth: "120px" });
      emiCols.push({ Header: () => headerNode(`EMI ${i} Expected`, `emi${i}_expected`), accessor: `emi${i}_expected`, minWidth: "120px" });
      emiCols.push({ Header: () => headerNode(`EMI ${i} Paid`, `emi${i}_paid`), accessor: `emi${i}_paid`, minWidth: "120px" });
      emiCols.push({ Header: () => headerNode(`EMI ${i} Paid Date`, `emi${i}_paidDate`), accessor: `emi${i}_paidDate`, minWidth: "120px" });
      emiCols.push({ Header: () => headerNode(`EMI ${i} Status`, `emi${i}_status`), accessor: `emi${i}_status`, minWidth: "100px" });
    }
    return [...base, ...emiCols];
  }, [sortKey, sortDir]);

  const data = useMemo(() => {
    return pageSlice.map((r) => ({
      ...r,
      invoiceNo: r.invoiceNo ?? "-",
      invoiceDate: r.invoiceDate ?? "-",
      serialNumber: r.serialNumber ?? "-",
      itemName: r.itemName ?? "-",
      paymentTerms: r.paymentTerms ?? "-",
      salesAgentCode: r.salesAgentCode ?? "-",
      knoxStatus: r.knoxStatus ?? "-",
      daysOverdue: r.daysOverdue ?? 0,
      emi1_dueDate: r.emi1_dueDate ?? "-",
      emi1_expected: r.emi1_expected ?? "-",
      emi1_paid: r.emi1_paid ?? "-",
      emi1_paidDate: r.emi1_paidDate ?? "-",
      emi1_status: r.emi1_status ?? "-",
      emi2_dueDate: r.emi2_dueDate ?? "-",
      emi2_expected: r.emi2_expected ?? "-",
      emi2_paid: r.emi2_paid ?? "-",
      emi2_paidDate: r.emi2_paidDate ?? "-",
      emi2_status: r.emi2_status ?? "-",
      emi3_dueDate: r.emi3_dueDate ?? "-",
      emi3_expected: r.emi3_expected ?? "-",
      emi3_paid: r.emi3_paid ?? "-",
      emi3_paidDate: r.emi3_paidDate ?? "-",
      emi3_status: r.emi3_status ?? "-",
      emi4_dueDate: r.emi4_dueDate ?? "-",
      emi4_expected: r.emi4_expected ?? "-",
      emi4_paid: r.emi4_paid ?? "-",
      emi4_paidDate: r.emi4_paidDate ?? "-",
      emi4_status: r.emi4_status ?? "-",
      emi5_dueDate: r.emi5_dueDate ?? "-",
      emi5_expected: r.emi5_expected ?? "-",
      emi5_paid: r.emi5_paid ?? "-",
      emi5_paidDate: r.emi5_paidDate ?? "-",
      emi5_status: r.emi5_status ?? "-",
      emi6_dueDate: r.emi6_dueDate ?? "-",
      emi6_expected: r.emi6_expected ?? "-",
      emi6_paid: r.emi6_paid ?? "-",
      emi6_paidDate: r.emi6_paidDate ?? "-",
      emi6_status: r.emi6_status ?? "-",
    }));
  }, [pageSlice]);

  const exportRows = useMemo(() => {
    return sortedAll.map((r) => ({
      invoiceNo: r.invoiceNo ?? "",
      invoiceDate: r.invoiceDate ?? "",
      serialNumber: r.serialNumber ?? "",
      itemName: r.itemName ?? "",
      paymentTerms: r.paymentTerms ?? "",
      salesAgentCode: r.salesAgentCode ?? "",
      knoxStatus: r.knoxStatus ?? "",
      daysOverdue: r.daysOverdue ?? "",
      emi1_dueDate: r.emi1_dueDate ?? "",
      emi1_expected: r.emi1_expected ?? "",
      emi1_paid: r.emi1_paid ?? "",
      emi1_paidDate: r.emi1_paidDate ?? "",
      emi1_status: r.emi1_status ?? "",
      emi2_dueDate: r.emi2_dueDate ?? "",
      emi2_expected: r.emi2_expected ?? "",
      emi2_paid: r.emi2_paid ?? "",
      emi2_paidDate: r.emi2_paidDate ?? "",
      emi2_status: r.emi2_status ?? "",
      emi3_dueDate: r.emi3_dueDate ?? "",
      emi3_expected: r.emi3_expected ?? "",
      emi3_paid: r.emi3_paid ?? "",
      emi3_paidDate: r.emi3_paidDate ?? "",
      emi3_status: r.emi3_status ?? "",
      emi4_dueDate: r.emi4_dueDate ?? "",
      emi4_expected: r.emi4_expected ?? "",
      emi4_paid: r.emi4_paid ?? "",
      emi4_paidDate: r.emi4_paidDate ?? "",
      emi4_status: r.emi4_status ?? "",
      emi5_dueDate: r.emi5_dueDate ?? "",
      emi5_expected: r.emi5_expected ?? "",
      emi5_paid: r.emi5_paid ?? "",
      emi5_paidDate: r.emi5_paidDate ?? "",
      emi5_status: r.emi5_status ?? "",
      emi6_dueDate: r.emi6_dueDate ?? "",
      emi6_expected: r.emi6_expected ?? "",
      emi6_paid: r.emi6_paid ?? "",
      emi6_paidDate: r.emi6_paidDate ?? "",
      emi6_status: r.emi6_status ?? "",
    }));
  }, [sortedAll]);

  return (
    <Box
      position="fixed"
      top="80px"
      left={{ base: 0, xl: sidebarCollapsed ? "80px" : "290px" }}
      right="0"
      bottom="0"
      display="flex"
      flexDirection="column"
      p={{ base: "20px", md: "30px" }}
      pe="20px"
      overflow="hidden"
    >
      <Box mb={3} flexShrink={0}>
        <Flex gap={3} alignItems="center" mb={3}>
          <Input
            placeholder="Search all columns"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            flex="1"
          />
          <FilterSystem
            filterFields={filterFields}
            onFilterChange={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
            initialFilters={filters}
            externalFilters={filters}
            showTags={false}
          />
        </Flex>
        {getActiveFilterCount(filters) > 0 && (
          <Wrap spacing={2}>
            {filterFields.map((field) => {
              if (field.type === "daterange") {
                const fromValue = filters[`${field.key}_from`];
                const toValue = filters[`${field.key}_to`];
                if (fromValue || toValue) {
                  return (
                    <WrapItem key={field.key}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        bg={useColorModeValue("blue.50", "blue.900")}
                        color={useColorModeValue("blue.700", "blue.200")}
                      >
                        <TagLabel>
                          {field.label}: {fromValue || "..."} to {toValue || "..."}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeFilterHelper(`${field.key}_from`, filters, setFilters, (newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }, filterFields)} />
                      </Tag>
                    </WrapItem>
                  );
                }
              } else {
                const value = filters[field.key];
                const hasValue = field.type === "multiselect"
                  ? Array.isArray(value) && value.length > 0
                  : value;

                if (hasValue) {
                  return (
                    <WrapItem key={field.key}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        bg={useColorModeValue("blue.50", "blue.900")}
                        color={useColorModeValue("blue.700", "blue.200")}
                      >
                        <TagLabel>
                          {field.label}: {getFilterDisplayValue(field, value)}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeFilterHelper(field.key, filters, setFilters, (newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }, filterFields)} />
                      </Tag>
                    </WrapItem>
                  );
                }
              }
              return null;
            })}
          </Wrap>
        )}
      </Box>
      <Box flex="1" minH="0">
        <MainTable
          loaded={!isEmiDetailsLoading}
          data={data}
          columns={columns}
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={page}
          perPage={perPage}
          perPageOptions={[50, 100, 200]}
          exports={exportRows}
          totalPage={totalPages}
          name={`EMI Details (${totalCount})`}
          containerHeight="100%"
        />
      </Box>
    </Box>
  );
}

export default EmiDetails;
